import { api, TaskItem, ProjectDetail, TaskStatus, Epic, Sprint } from '../api';
import { renderMarkdown } from '../lib/markdown';
import { statusMeta, STATUSES } from '../lib/status';
import { escapeHtml, formatDate, initials } from '../lib/utils';
import { flattenTree, descendants } from '../lib/taskTree';
import { openModal } from './modal';
import { openTaskForm } from './taskForm';
import { toast } from './toast';

let sprintsCache: { projectId: string; sprints: Sprint[] } | null = null;

async function ensureSprints(projectId: string): Promise<Sprint[]> {
  if (sprintsCache && sprintsCache.projectId === projectId) return sprintsCache.sprints;
  try {
    const res = await api.get<{ sprints: Sprint[] }>(`/projects/${projectId}/sprints`);
    sprintsCache = { projectId, sprints: res.sprints };
  } catch {
    sprintsCache = { projectId, sprints: [] };
  }
  return sprintsCache.sprints;
}

export function openTaskModal(
  task: TaskItem,
  project: ProjectDetail,
  tasks: TaskItem[],
  epics: Epic[],
  onChanged: () => void,
  readOnly = false,
  canComment = false
): void {
  let current: TaskItem = task;
  let editing = false;
  let closeModal: (() => void) | null = null;
  let sprints: Sprint[] = [];

  const children = () => tasks.filter((t) => t.parentId === current.id);
  const parent = () => tasks.find((t) => t.id === current.parentId) || null;
  const epic = () => epics.find((e) => e.id === current.epicId) || null;

  function assigneeOptions(): string {
    return [
      '<option value="">Unassigned</option>',
      ...project.members.map(
        (m) =>
          `<option value="${escapeHtml(m.id)}" ${
            current.assigneeId === m.id ? 'selected' : ''
          }>${escapeHtml(m.name)}</option>`
      ),
    ].join('');
  }

  function epicOptions(): string {
    return [
      '<option value="">No epic</option>',
      ...epics.map(
        (e) =>
          `<option value="${escapeHtml(e.id)}" ${
            current.epicId === e.id ? 'selected' : ''
          }>${escapeHtml(e.name)}</option>`
      ),
    ].join('');
  }

  function sprintOptions(): string {
    return [
      '<option value="">No sprint</option>',
      ...sprints.map(
        (s) =>
          `<option value="${escapeHtml(s.id)}" ${
            current.sprintId === s.id ? 'selected' : ''
          }>${escapeHtml(s.name)}</option>`
      ),
    ].join('');
  }

  function parentOptions(): string {
    const excluded = descendants(tasks, current.id);
    excluded.add(current.id);
    const available = flattenTree(tasks).filter(({ task }) => !excluded.has(task.id));
    return [
      '<option value="">No parent (top-level task)</option>',
      ...available.map(
        ({ task, depth }) =>
          `<option value="${escapeHtml(task.id)}" ${
            current.parentId === task.id ? 'selected' : ''
          }>${'· '.repeat(depth)}${escapeHtml(task.title)}</option>`
      ),
    ].join('');
  }

  function statusOptions(): string {
    return STATUSES.map(
      (s) =>
        `<option value="${s.key}" ${current.status === s.key ? 'selected' : ''}>${s.label}</option>`
    ).join('');
  }

  function render(): void {
    const meta = statusMeta(current.status);
    const body = document.querySelector('#modal-root .modal-body') as HTMLElement;
    if (!body) return;

    const kids = children();
    const parentTask = parent();
    const ep = epic();
    const sp = sprints.find((s) => s.id === current.sprintId) || null;

    const commentsHtml = (): string => {
      const comments = current.comments || [];
      return `
        <div style="margin-top:24px;border-top:1px solid var(--border);padding-top:18px;">
          <h3 style="font-size:14px;font-weight:700;margin:0 0 12px;">Comments (${comments.length})</h3>
          ${
            comments.length === 0
              ? '<p class="muted" style="font-size:14px;">No comments yet.</p>'
              : comments
                  .map(
                    (c) => `
            <div class="comment">
              <div class="comment-head">
                <span class="avatar">${initials(c.authorName)}</span>
                <strong>${escapeHtml(c.authorName)}</strong>
                <span class="muted" style="font-size:12px;">${formatDate(c.createdAt)}</span>
              </div>
              <div class="markdown-body comment-body">${renderMarkdown(c.body)}</div>
            </div>
          `
                  )
                  .join('')
          }
          ${
            canComment && !editing
              ? `
          <form id="comment-form" style="margin-top:14px;">
            <textarea class="textarea" name="body" rows="3" placeholder="Add a comment… (Markdown supported)"></textarea>
            <div style="display:flex;justify-content:flex-end;margin-top:10px;">
              <button class="btn btn-primary btn-sm" type="submit">Add comment</button>
            </div>
          </form>
          `
              : ''
          }
        </div>
      `;
    };

    body.innerHTML = `
      <div class="task-detail">
        <div class="task-detail-head">
          <div class="badges">
            <span class="badge" style="color:${meta.color};background:${meta.bg}">
              <span class="dot"></span>${meta.label}
            </span>
            <span class="points-chip">${current.storyPoints} pts</span>
            ${ep ? `<span class="badge" style="color:#7c3aed;background:#f5f3ff;">◆ ${escapeHtml(ep.name)}</span>` : ''}
            ${sp ? `<span class="badge" style="color:#0e7490;background:#ecfeff;">⚡ ${escapeHtml(sp.name)}</span>` : ''}
          </div>
          <div class="task-detail-actions">
            ${
              readOnly
                ? ''
                : `${editing ? '' : '<button class="btn btn-ghost btn-sm" data-action="edit">Edit</button>'}
                   <button class="btn btn-danger btn-sm" data-action="delete">Delete</button>`
            }
          </div>
        </div>

        ${
          editing
            ? `
          <div class="field">
            <label>Title</label>
            <input class="input" data-field="title" value="${escapeHtml(current.title)}" />
          </div>
          <div class="field">
            <label>Status</label>
            <select class="select" data-field="status">${statusOptions()}</select>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
            <div class="field">
              <label>Story points</label>
              <input class="input" type="number" min="0" data-field="points" value="${current.storyPoints}" />
            </div>
            <div class="field">
              <label>Assignee</label>
              <select class="select" data-field="assignee">${assigneeOptions()}</select>
            </div>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
            <div class="field">
              <label>Epic</label>
              <select class="select" data-field="epic">${epicOptions()}</select>
            </div>
            <div class="field">
              <label>Parent task</label>
              <select class="select" data-field="parent">${parentOptions()}</select>
            </div>
          </div>
          <div class="field">
            <label>Sprint</label>
            <select class="select" data-field="sprint">${sprintOptions()}</select>
          </div>
          <div class="field">
            <label>Description (Markdown supported)</label>
            <textarea class="textarea" rows="10" data-field="description">${escapeHtml(
              current.description
            )}</textarea>
          </div>
          <div style="display:flex;gap:10px;justify-content:flex-end;">
            <button class="btn btn-ghost" data-action="cancel">Cancel</button>
            <button class="btn btn-primary" data-action="save">Save changes</button>
          </div>
        `
            : `
          <h2 class="task-title">${escapeHtml(current.title)}</h2>
          <div class="task-meta-row">
            <span>Assignee: <strong>${escapeHtml(
              current.assigneeName || 'Unassigned'
            )}</strong></span>
            ${parentTask ? `<span>Subtask of: <strong>${escapeHtml(parentTask.title)}</strong></span>` : ''}
            <span>Updated: ${formatDate(current.updatedAt)}</span>
          </div>
          <div class="markdown-body">${renderMarkdown(current.description)}</div>

          <div style="margin-top:24px;border-top:1px solid var(--border);padding-top:18px;">
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;">
              <h3 style="font-size:14px;font-weight:700;margin:0;">Subtasks (${kids.length})</h3>
              ${
                readOnly
                  ? ''
                  : '<button class="btn btn-ghost btn-sm" data-action="add-subtask">+ Add subtask</button>'
              }
            </div>
            ${
              kids.length === 0
                ? '<p class="muted" style="font-size:14px;">No subtasks yet. Break this task into smaller pieces.</p>'
                : `
              <div style="display:flex;flex-direction:column;gap:6px;">
                ${kids
                  .map((k) => {
                    const km = statusMeta(k.status);
                    return `
                    <button class="subtask-row" data-subtask="${k.id}" style="display:flex;align-items:center;justify-content:space-between;gap:10px;width:100%;text-align:left;background:var(--surface-2);border:1px solid var(--border);border-radius:8px;padding:10px 12px;cursor:pointer;font-family:var(--font);font-size:13px;color:var(--text);">
                      <span style="font-weight:600;">${escapeHtml(k.title)}</span>
                      <span style="display:flex;gap:8px;align-items:center;">
                        <span class="badge" style="color:${km.color};background:${km.bg};"><span class="dot"></span>${km.label}</span>
                        <span class="points-chip">${k.storyPoints}</span>
                      </span>
                    </button>
                  `;
                  })
                  .join('')}
              </div>
            `
            }
          </div>
          ${commentsHtml()}
        `
        }
      </div>
    `;

    if (!readOnly) {
      body.querySelector('[data-action="edit"]')?.addEventListener('click', () => {
        editing = true;
        render();
      });
      body.querySelector('[data-action="cancel"]')?.addEventListener('click', () => {
        editing = false;
        render();
      });
      body.querySelector('[data-action="add-subtask"]')?.addEventListener('click', () => {
        openTaskForm(project, {
          initialStatus: current.status,
          epicId: current.epicId,
          parentId: current.id,
          epics,
          tasks,
          onChanged,
        });
      });
      body.querySelector('[data-action="delete"]')?.addEventListener('click', async () => {
        if (!confirm('Delete this task? Its subtasks will be kept and moved to the top level.')) return;
        try {
          await api.del(`/projects/${project.id}/tasks/${current.id}`);
          toast('Task deleted');
          closeModal?.();
          onChanged();
        } catch (e: any) {
          toast(e.message || 'Could not delete task', 'error');
        }
      });
    }

    body.querySelector('#comment-form')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const form = e.target as HTMLFormElement;
      const data = new FormData(form);
      const bodyText = String(data.get('body') || '').trim();
      if (!bodyText) return;
      const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Posting…';
      try {
        const res = await api.post<{ task: TaskItem }>(
          `/projects/${project.id}/tasks/${current.id}/comments`,
          { body: bodyText }
        );
        current = res.task;
        toast('Comment added');
        render();
      } catch (err: any) {
        toast(err.message || 'Could not add comment', 'error');
        submitBtn.disabled = false;
        submitBtn.textContent = 'Add comment';
      }
    });

    body.querySelector('[data-action="save"]')?.addEventListener('click', async () => {
      const get = (name: string) =>
        (body.querySelector(`[data-field="${name}"]`) as HTMLInputElement | null)?.value;
      const title = (get('title') || '').trim();
      if (!title) {
        toast('Title is required', 'error');
        return;
      }
      const payload: Record<string, unknown> = {
        title,
        status: get('status'),
        storyPoints: Number(get('points')) || 0,
        assigneeId: get('assignee') || null,
        epicId: get('epic') || null,
        parentId: get('parent') || null,
        sprintId: get('sprint') || null,
        description: get('description') || '',
      };
      try {
        const { task: updated } = await api.patch<{ task: TaskItem }>(
          `/projects/${project.id}/tasks/${current.id}`,
          payload
        );
        current = updated;
        editing = false;
        toast('Task saved');
        render();
        onChanged();
      } catch (e: any) {
        toast(e.message || 'Could not save task', 'error');
      }
    });

    body.querySelectorAll<HTMLElement>('[data-subtask]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const child = tasks.find((t) => t.id === btn.dataset.subtask!);
        if (child) {
          closeModal?.();
          openTaskModal(child, project, tasks, epics, onChanged, readOnly, canComment);
        }
      });
    });
  }

  closeModal = openModal('<div class="spinner">Loading…</div>', () => {
    void (async () => {
      sprints = await ensureSprints(project.id);
      render();
    })();
  });
}
